import { filter, delay, first, take } from 'rxjs/operators';
import { WindowService } from '../../services/window.service';
import { Injectable, NgZone } from '@angular/core';
import { SettingsService } from '../../services/settings.service';
import { CityService } from '../../services/location/city-lookup.service';
import { Place } from '../../classes/place.class';
import {
  ReplaySubject,
  BehaviorSubject,
  Subscription,
  interval,
  Observable,
} from 'rxjs';
import { DynamicAssetLoaderService } from '../../services/dynamic-asset-loader/dynamic-asset-loader.service';
import { MembersService } from '../../services/members.service';
import { DelightedProperties } from '@interfaces/delighted-properties.interface';
import { DelightedCombinedProperties } from '@interfaces/delighted-combined-properties.model';
import { Results } from '@interfaces/results.interface';
import { DelightedMemberProperties } from '@interfaces/delighted-member-properties.model';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class DelightedService {
  readonly sessionStorage: Storage;
  private currentProperties: DelightedProperties;
  private memberPropertiesSubject: ReplaySubject<DelightedMemberProperties>;
  private searchProperties = new BehaviorSubject<any>({});
  private sessionStorageDisabledKey: string = 'delighted.isDisabled';
  private setting = new BehaviorSubject<boolean>(false);
  private surveySubscriptions: {
    csat?: Subscription;
    nps?: Subscription;
    dme?: Subscription;
  } = {};

  constructor(
    private window: WindowService,
    private membersService: MembersService,
    private settingsService: SettingsService,
    private cityService: CityService,
    private assetLoader: DynamicAssetLoaderService,
    private ngZone: NgZone
  ) {
    this.sessionStorage = this.window['sessionStorage'];
    this.subscribeToSetting();
    this.subscribeToMemberProperties();
  }

  public activateSurvey(
    properties: DelightedProperties,
    searchResults: Results,
    delightedDelay: number,
    surveyType: string
  ): void {
    this.setting.subscribe((setting) => {
      if (!setting || this.isDisabled()) {
        return;
      }
      this.currentProperties = new DelightedCombinedProperties(
        this.mergedProperties(properties, searchResults, surveyType),
        this.sessionStorage
      );
      this.reset(surveyType);
      this.surveySubscriptions[surveyType] = this.subscribeToSurvey(
        delightedDelay,
        surveyType
      );
    });
  }

  public disable(): void {
    this.reset('nps');
    this.reset('csat');
    this.reset('dme');
    this.sessionStorage.setItem(this.sessionStorageDisabledKey, 'true');
  }

  public reset(type: string): void {
    if (this.surveySubscriptions[type]) {
      this.surveySubscriptions[type].unsubscribe();
    }
  }

  private subscribeToSurvey(
    delightedDelay: number,
    surveyType: string
  ): Subscription {
    return this.ngZone.runOutsideAngular(() => {
      return this.getSearchProperties(delightedDelay).subscribe(
        (searchProperties) => {
          this.deleteDelighted();
          this.loadAsset(surveyType);
          this.ngZone.run(() => {
            this.waitForDelightedWindow().subscribe(
              () => {
                this.currentProperties.properties = Object.assign(
                  searchProperties,
                  this.currentProperties.properties
                );
                this.window['delighted'].survey(this.currentProperties);
              },
              () => {
                return;
              }
            );
          });
        }
      );
    });
  }

  private getSearchProperties(delightedDelay: number): Observable<any> {
    return this.searchProperties.pipe(first(), delay(delightedDelay));
  }

  private waitForDelightedWindow(): Observable<number> {
    return interval(50).pipe(
      take(10),
      first(() => !!this.window['delighted'])
    );
  }

  private isDisabled(): boolean {
    return (
      this.sessionStorage.getItem(this.sessionStorageDisabledKey) === 'true' ||
      this.sessionStorage.getItem('surveyShown') === 'true'
    );
  }

  private subscribeToMemberProperties(): void {
    this.memberPropertiesSubject = new ReplaySubject(1);
    this.membersService.member
      .pipe(first((member) => !!member))
      .subscribe((member) =>
        this.memberPropertiesSubject.next(new DelightedMemberProperties(member))
      );
  }

  private subscribeToSetting(): void {
    this.settingsService
      .getSetting('delighted_feedback_integration')
      .subscribe((res) => this.setting.next(!!res));
  }

  private mergedProperties(
    delightedProperties: DelightedProperties,
    searchResults: Results,
    surveyType: string
  ): DelightedProperties {
    let out: DelightedProperties = Object.assign({}, delightedProperties);
    if (searchResults.searchParams) {
      out = this.subscribeToCityService(
        searchResults.searchParams.get('geo_location'),
        out
      );
      out.properties.radius = searchResults.searchParams.get('radius');
      out.properties.locale = searchResults.searchParams.get('data_language');
    }
    this.setMemberProperties(out);
    out['onShow'] = () => {
      this.onShowCallback(surveyType);
    };
    return out;
  }

  private subscribeToCityService(
    coordinates: string,
    params: DelightedProperties
  ): DelightedProperties {
    this.cityService
      .forPlace(new Place({ geo: coordinates }))
      .pipe(filter((results) => !!(results && results.length)))
      .subscribe((results: Place[]) => {
        params.properties.zipcode = results[0].zip;
        this.setSearchProperties(params);
      });
    return params;
  }

  private setSearchProperties(params: DelightedProperties): void {
    const {
      properties: { radius, locale, zipcode, searchType, searchTerm },
    } = params;
    const newSearchProperties = {
      radius: radius,
      locale: locale,
      zipcode: zipcode,
      searchType: searchType,
      searchTerm: searchTerm,
    };
    if (!_.isEqual(this.searchProperties.getValue(), newSearchProperties)) {
      this.searchProperties.next(newSearchProperties);
    }
  }

  private setMemberProperties(properties: DelightedProperties): void {
    this.memberPropertiesSubject.subscribe((memberProperties) => {
      if (memberProperties.fullName) {
        properties.name = memberProperties.fullName;
      }
      Object.assign(properties.properties, memberProperties);
      return properties;
    });
  }

  private onShowCallback(surveyType: string): void {
    this.sessionStorage.setItem('surveyShown', 'true');
    if (surveyType === 'csat') {
      this.reset('nps');
    }
  }

  private deleteDelighted(): void {
    delete this.window['delighted'];
  }

  private loadAsset(surveyType: string): void {
    this.assetLoader.loadAsset(
      '../../../assets/scripts/delighted-' + surveyType + '.js',
      'script'
    );
  }
}
